import { useEffect, useCallback, useState } from "react";
import { useSocketInstance } from "../context/SocketContext";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { io } from "socket.io-client";
import ChatApi from "../services/chatService";
import NotifiactionApi from "../services/notofiactionService";
import { addMoreChatRoom, selectAllChatRoom, updateCountUnreadMessege, updateMessegeUnreadCount } from "../bmr/slices/chat";
import { API_BASE_URL } from '../bmr/constants/api.constants';
import { hasCRApprovePermission } from "./utils";

const SocketManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const [allRoomJoin, setAllRoomJoin] = useState(false);

  const {
    socketInstance,
    setSocketInstance,
    setTaskManager,
    setCRTaskManager,
    setMessageRead,
    setNotifiactionCount,
  } = useSocketInstance();

  const { loginId: loginID, role_id } = useSelector((state) => state.loginData);
    const crPermissions = useSelector((state) => state.loginData?.cr_permissions);
  const allChatRoom = useSelector(selectAllChatRoom);
  const jwtToken = localStorage.getItem("user");


  //  Initialize socket connection
  useEffect(() => {
    if (jwtToken && !socketInstance) {
      const socket = io(API_BASE_URL, {
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        extraHeaders: {
          Authorization: jwtToken,
        },
      });

      socket.on("connect", () => {
        if (socket.connected) {
          setSocketInstance(socket);
        }
      });
    }
  }, [jwtToken, socketInstance, setSocketInstance]);

  //  Fetch initial notification and message status
  useEffect(() => {
    const fetchNotificationStatus = async () => {
      try {
        const res = await NotifiactionApi.countNotifiaction();
        setNotifiactionCount(res?.data);
      } catch (e) {
        console.error("Error fetching notifications", e);
      }
    };

    const fetchMessageStatus = async () => {
      try {
        const res = await ChatApi.messageStatus();
        setMessageRead(res?.data?.isNewMessage || false);
      } catch (e) {
        console.error("Error fetching message status", e);
      }
    };

    if (jwtToken) {
      fetchNotificationStatus();
      fetchMessageStatus();
    }
  }, [jwtToken, setNotifiactionCount, setMessageRead]);

  //  Memoized handlers
  const handleReceiveNotification = useCallback((data) => {
    setNotifiactionCount(data);
  }, [setNotifiactionCount]);

  const handleReceiveChatRoom = useCallback(async (data) => {
    const exists = allChatRoom.some(item => item.id === data.id);
    if (!exists) {
      await dispatch(addMoreChatRoom(data));
      socketInstance?.emit("join-group", data?.name);
    }
  }, [allChatRoom, dispatch, socketInstance]);

  const handleReceiveMessage = useCallback((data) => {
    const dateKey = Object.keys(data)[0];
    const message = data[dateKey]?.[0];
    if (loginID === message?.receiver_id) {
      setMessageRead(message?.messageCount > 0);
    }
  }, [loginID, setMessageRead]);

  const handleReceiveTasks = useCallback((data) => {
    setTaskManager(data);
  }, [setTaskManager]);


   const handleCRReceiveTasks = useCallback((data) => {
    setCRTaskManager(data);
  }, [setCRTaskManager]);

  const handleReceiveUnreadCount = useCallback(async (data) => {
    await dispatch(updateCountUnreadMessege(data));
  }, [dispatch]);

  const handleReceiveUnreadMessage = useCallback(async (data) => {
    await dispatch(updateMessegeUnreadCount(data));
  }, [dispatch]);

  // Set up socket event listeners
  useEffect(() => {
    if (!socketInstance || !loginID) return;

    // Join all chat rooms once they are loaded
    if (!allRoomJoin && allChatRoom.length > 0) {
      allChatRoom.forEach(op => {
        socketInstance.emit("join-group", op?.name);
      });
      setAllRoomJoin(true);
    }

    // Always-needed listeners
    socketInstance.off("receive_notification", handleReceiveNotification);
    socketInstance.off("room-created", handleReceiveChatRoom);
    socketInstance.off("unread_count", handleReceiveUnreadCount);
    socketInstance.off("unready_message", handleReceiveUnreadMessage);

    socketInstance.on("receive_notification", handleReceiveNotification);
    socketInstance.on("room-created", handleReceiveChatRoom);
    socketInstance.on("unread_count", handleReceiveUnreadCount);
    socketInstance.on("unready_message", handleReceiveUnreadMessage);

    if (pathname !== "/message") {
      // Register message/task/CR listeners
      socketInstance.off("receive_message", handleReceiveMessage);
      socketInstance.off("receive_cr_list", handleCRReceiveTasks);
      socketInstance.off("receive_task_list", handleReceiveTasks);

      socketInstance.on("receive_message", handleReceiveMessage);
      socketInstance.on("receive_cr_list", handleCRReceiveTasks);
      socketInstance.on("receive_task_list", handleReceiveTasks);

      // ✅ Single unified emit block — no duplicate emits
      if (role_id === 1 || hasCRApprovePermission(crPermissions)) {
        socketInstance.emit("count_task");
      }

      socketInstance.emit("notify_task_list_count");

      return () => {
        socketInstance.off("receive_message", handleReceiveMessage);
        socketInstance.off("receive_cr_list", handleCRReceiveTasks);
        socketInstance.off("receive_task_list", handleReceiveTasks);
        socketInstance.off("receive_notification", handleReceiveNotification);
        socketInstance.off("room-created", handleReceiveChatRoom);
        socketInstance.off("unread_count", handleReceiveUnreadCount);
        socketInstance.off("unready_message", handleReceiveUnreadMessage);
      };
    }

    // Cleanup for always-needed listeners when on /message
    return () => {
      socketInstance.off("receive_notification", handleReceiveNotification);
      socketInstance.off("room-created", handleReceiveChatRoom);
      socketInstance.off("unread_count", handleReceiveUnreadCount);
      socketInstance.off("unready_message", handleReceiveUnreadMessage);
    };
  }, [
    socketInstance,
    loginID,
    pathname,
    role_id,
    allRoomJoin,
    allChatRoom,
    handleReceiveNotification,
    handleReceiveChatRoom,
    handleReceiveMessage,
    handleCRReceiveTasks,
    handleReceiveTasks,
    handleReceiveUnreadCount,
    handleReceiveUnreadMessage,
    crPermissions,
  ]);

  return null;
};

export default SocketManager;